import { getMedicalHistoryProgressSummary } from "@src/packages/medical-history/sectionRegistry";

export type CarrierCompensationSignals = {
  basicInfo: boolean;
  pregnancyHistory: boolean;
  medicationsAndAllergies: boolean;
  medicalAndSurgicalHistory: boolean;
};

export type CarrierCompensationReadiness = {
  completedCount: number;
  totalCount: number;
  percent: number;
  isReady: boolean;
  sections: {
    id: keyof CarrierCompensationSignals;
    label: string;
    complete: boolean;
  }[];
};

export function resolveCarrierCompensationReadiness(
  signals: CarrierCompensationSignals,
): CarrierCompensationReadiness {
  const sections = [
    {
      id: "basicInfo" as const,
      label: "Basic profile",
      complete: signals.basicInfo,
    },
    {
      id: "pregnancyHistory" as const,
      label: "Pregnancy and birth history",
      complete: signals.pregnancyHistory,
    },
    {
      id: "medicationsAndAllergies" as const,
      label: "Medication and allergy history",
      complete: signals.medicationsAndAllergies,
    },
    {
      id: "medicalAndSurgicalHistory" as const,
      label: "Medical and surgical history",
      complete: signals.medicalAndSurgicalHistory,
    },
  ];
  const completedCount = sections.filter((section) => section.complete).length;
  const totalCount = sections.length;

  return {
    completedCount,
    totalCount,
    percent: Math.round((completedCount / totalCount) * 100),
    isReady: completedCount === totalCount,
    sections,
  };
}

export function deriveCarrierCompensationSignals(
  patient: Parameters<typeof getMedicalHistoryProgressSummary>[0],
): CarrierCompensationSignals {
  const { completionData } = getMedicalHistoryProgressSummary(patient);

  return {
    basicInfo: completionData.basicInfo.percent >= 1,
    pregnancyHistory: completionData.pregnancy.percent >= 1,
    medicationsAndAllergies:
      completionData.medicationsVaccines.percent >= 1 &&
      completionData.allergies.percent >= 1,
    medicalAndSurgicalHistory:
      completionData.diseaseHistory.percent >= 1 &&
      completionData.surgeries.percent >= 1,
  };
}
